import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, BookOpen, Trophy, Zap, Wand2, Gift, Copy, Check, Gem } from "lucide-react";
import { toast } from "sonner";
import { PayoutWidget } from "@/components/PayoutWidget";
import { StreaksCard } from "@/components/StreaksCard";
import { MySubmissions } from "@/components/MySubmissions";

export default function Dashboard() {
  const { user } = useAuth();
  const [myStories, setMyStories] = useState([]);
  const [referral, setReferral] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user) {
      api.get("/me/stories").then(r => setMyStories(r.data)).catch(() => {});
      api.get("/me/referral").then(r => setReferral(r.data)).catch(() => {});
    }
  }, [user]);

  const copyLink = async () => {
    if (!referral) return;
    const url = `${window.location.origin}/?ref=${referral.code}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Invite link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — long-press the link");
    }
  };

  if (!user) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center">
        <h1 className="font-serif text-3xl font-bold mb-3">Sign in to see your dashboard</h1>
        <Link to="/login?next=/dashboard" data-testid="dash-signin" className="inline-flex items-center justify-center rounded-full bg-amber-500 text-black font-semibold px-7 h-12">Sign in to continue</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16">
      <div className="mb-10">
        <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-amber-400 mb-2">Welcome back</div>
        <h1 className="font-serif text-3xl md:text-5xl font-bold tracking-tight">{user.name}</h1>
      </div>

      <div className="grid md:grid-cols-3 gap-4 md:gap-6 mb-12">
        <StatCard icon={Gem} tone="amber" label="Gems balance" value={user.credits} to="/gems" />
        <StatCard icon={BookOpen} tone="sky" label="Stories published" value={myStories.length} />
        <StatCard icon={Trophy} tone="rose" label="Subscription" value={user.subscription_status === "free" ? "Free reader" : user.subscription_status} />
      </div>

      <StreaksCard />

      <MySubmissions />

      <PayoutWidget />

      <div className="rounded-3xl border border-white/10 bg-[#0F1117] p-6 md:p-10 mb-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-sky-400 mb-1">Create free</div>
          <h2 className="font-serif text-2xl font-semibold">Weave another world</h2>
          <p className="text-slate-400 text-sm">Publish a story — earn 50 credits. Submit to the contest — earn 100.</p>
        </div>
        <Button data-testid="dash-create" asChild className="bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-full h-11 px-6">
          <Link to="/create"><Wand2 className="h-4 w-4 mr-2" /> Start writing</Link>
        </Button>
      </div>

      {referral && (
        <div data-testid="referral-card" className="rounded-3xl border border-rose-500/25 bg-gradient-to-br from-rose-500/8 via-transparent to-amber-500/8 p-6 md:p-10 mb-12">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex-1">
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-rose-400 mb-1 flex items-center gap-2"><Gift className="h-3 w-3" /> Invite friends</div>
              <h2 className="font-serif text-2xl font-semibold">Give a taste, get a free week</h2>
              <p className="text-slate-400 text-sm mt-1 max-w-xl">Share your invite link. When a friend subscribes, we add <span className="text-amber-300 font-medium">7 free days</span> onto your plan (or start one for you). They start with <span className="text-amber-300 font-medium">+100 bonus credits</span>.</p>
            </div>
            <div className="w-full md:w-auto">
              <div className="rounded-xl border border-white/10 bg-[#07080A] p-3 flex items-center gap-3">
                <span data-testid="referral-code" className="font-mono text-sm text-amber-300 truncate">{`${window.location.origin}/?ref=${referral.code}`}</span>
                <Button data-testid="copy-referral" size="sm" onClick={copyLink} variant="outline" className="rounded-full border-amber-500/40 text-amber-300 hover:bg-amber-500/10 h-8 px-3">
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                </Button>
              </div>
              <div className="mt-2 text-[11px] text-slate-500 font-mono uppercase tracking-widest">{referral.signups} friend{referral.signups === 1 ? "" : "s"} joined</div>
            </div>
          </div>
        </div>
      )}

      <h2 className="font-serif text-2xl md:text-3xl font-semibold mb-6">Your stories</h2>
      {myStories.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center text-slate-500">
          No stories yet. <Link to="/create" className="text-amber-300 hover:underline">Write your first one →</Link>
        </div>
      ) : (
        <div data-testid="my-stories" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {myStories.map((s) => (
            <Link key={s.story_id} to={`/story/${s.story_id}`} className="rounded-2xl border border-white/10 bg-[#0F1117] p-5 glow-hover">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-400 mb-1">{s.genre}</div>
              <div className="font-serif text-xl font-semibold mb-1">{s.title}</div>
              <div className="text-xs text-slate-400 line-clamp-2">{s.tagline}</div>
              <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500">
                <span>{new Date(s.created_at).toLocaleDateString()}</span>
                <span className="flex items-center gap-1 text-rose-400"><Zap className="h-3 w-3" /> {s.votes || 0} votes</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

const StatCard = ({ icon: Icon, tone, label, value, to }) => {
  const tones = { amber: "text-amber-400", sky: "text-sky-400", rose: "text-rose-400" };
  const inner = (
    <>
      <Icon className={`h-6 w-6 ${tones[tone]} mb-3`} />
      <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-1">{label}</div>
      <div className="font-serif text-3xl font-bold">{value}</div>
      {to && <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-amber-400">Top up →</div>}
    </>
  );
  if (to) return <Link to={to} className="rounded-2xl bg-[#0F1117] border border-white/10 p-6 glow-hover block">{inner}</Link>;
  return <div className="rounded-2xl bg-[#0F1117] border border-white/10 p-6 glow-hover">{inner}</div>;
};
